import api from './api';
import { isAxiosError } from 'axios';

export type DeliveryMode = 'OnDemand' | 'Live';

/**
 * BPE-150 contract-resolution error codes returned on HTTP 409 when a
 * registration (start / restart / live-webinar register) can't resolve a
 * single active contract for the learner. Frontend maps these to
 * user-actionable copy — do not hard-code English in call sites, use
 * `describeContractError` so we have one place to update wording.
 */
export type ContractErrorCode = 'NO_ACTIVE_CONTRACT' | 'AMBIGUOUS_CONTRACT';

export interface ContractError {
  code: ContractErrorCode;
  message: string;
  conflictingContractIds?: number[];
}

/**
 * Inspects a caught error and returns a structured ContractError when the
 * response is an HTTP 409 with one of the contract-resolution error codes.
 * Returns null otherwise so callers can fall through to generic handling.
 */
export function parseContractError(err: unknown): ContractError | null {
  if (!isAxiosError(err) || err.response?.status !== 409) return null;
  const data = err.response.data as {
    errorCode?: string;
    message?: string;
    conflictingContractIds?: number[];
  } | undefined;
  if (data?.errorCode !== 'NO_ACTIVE_CONTRACT' && data?.errorCode !== 'AMBIGUOUS_CONTRACT') {
    return null;
  }
  return {
    code: data.errorCode,
    message: data.message ?? describeContractError(data.errorCode),
    conflictingContractIds: data.conflictingContractIds,
  };
}

/**
 * Fallback copy when the backend payload is missing a `message` field.
 * Call sites should prefer `error.message` from `parseContractError` result,
 * which reflects whatever copy the backend shipped.
 */
export function describeContractError(code: ContractErrorCode): string {
  switch (code) {
    case 'NO_ACTIVE_CONTRACT':
      return "Your organization doesn't have an active enrollment period — please contact your admin.";
    case 'AMBIGUOUS_CONTRACT':
      return "We found multiple active contracts for you. Your admin needs to resolve this before you can register — please reach out.";
  }
}

/**
 * Why a certificate cannot currently be claimed (BPE-571). Stable wire codes from the
 * server's ClaimBlockReason enum — branch on these, never on the human-readable message.
 */
export type ClaimBlockReason =
  | 'NOT_STARTED'
  | 'ENROLLMENT_NOT_FOR_CREDIT'
  | 'NO_MATCHING_JURISDICTION'
  | 'VIDEO_INCOMPLETE'
  | 'CHECKPOINTS_INCOMPLETE'
  | 'EXAM_NOT_PASSED'
  | 'ATTENDANCE_INCOMPLETE'
  | 'ALREADY_CLAIMED'
  | 'CREDIT_EARNED_ON_OTHER_DELIVERY'
  | 'CONTRACT_INACTIVE'
  | 'COURSE_NOT_COMPLETED'
  | 'NOT_ACCREDITED';

/**
 * Server-computed claim eligibility for one enrollment (BPE-571).
 *
 * This is authoritative — render it, do not re-derive it. Before it existed the rule lived in
 * six places server-side plus two client predicates, and they disagreed: the course page could
 * offer a Claim Certificate button for a viewing the server would refuse, and (the BPE-571
 * defect) accept one it should have refused.
 */
export interface CreditEligibility {
  /**
   * False when nothing the learner does next on this enrollment can produce a certificate —
   * e.g. it was started with no credit type stamped because no jurisdiction matched. The remedy
   * is to restart the course. Drives the no-credit warning and the player's just-watching mode.
   */
  canEarnCredit: boolean;
  /** True only when every claim requirement is satisfied right now. */
  canClaimNow: boolean;
  /** Why the claim is blocked; null when `canClaimNow` is true. */
  reason: ClaimBlockReason | null;
}

// Response from GET /api/progress/learning-objects/{id}
export interface CourseProgressResponse {
  progressId: number;
  learningObjectId: number;
  userId: number;
  progressPercent: number;
  currentPositionSeconds: number;
  totalWatchTimeSeconds: number;
  startedAt: string | null;
  checkpoint1CompletedAt: string | null;
  checkpoint2CompletedAt: string | null;
  videoCompletedAt: string | null;
  cpeExamPassedAt: string | null;
  surveyCompletedAt: string | null;
  completedAt: string | null;
  lastAccessedAt: string | null;
  status: string;
  /** Mirrors `creditEligibility.canClaimNow`; prefer `creditEligibility` so you also get the reason. */
  canClaimCertificate: boolean;
  /** BPE-571: the authoritative claim verdict. Optional so an older server response degrades safely. */
  creditEligibility?: CreditEligibility | null;
  hasCertificate: boolean;
  /** "CLE", "CPE", or null (just watching / course has no checkpoints). */
  creditType: string | null;
  /** "OnDemand" or "Live". */
  deliveryMode: DeliveryMode;
  /**
   * Delivery-type-specific resume/runtime state as a JSON string (shape depends on
   * the active delivery's platform, identified by a `type` discriminator). Used to
   * resume SCORM modules. Null until the player first persists state.
   */
  progressData: string | null;
  /**
   * True when the learner already earned a cert on another delivery of this
   * course (e.g. attended Live, now viewing OnDemand). The detail page treats
   * this delivery as a view-only re-watch: no checkpoints, no cert claim.
   */
  creditBlockedByOtherDelivery: boolean;
}

// Response from POST /api/progress/learning-objects/{id}/start
export interface CourseStartResponse {
  progressId: number;
  startedAt: string;
  creditType: string | null;
}

// Response from PUT /api/progress/learning-objects/{id}
export interface ProgressUpdateResponse {
  progressPercent: number;
  currentPosition: number;
  furthestWatched: number;
}

// Response from POST /api/progress/learning-objects/{id}/complete
export interface VideoCompleteResponse {
  videoCompletedAt: string | null;
  completedAt: string | null;
  canClaimCertificate: boolean;
  hasCertificate: boolean;
}

// Response from POST /api/progress/learning-objects/{id}/reset
export interface SimulationProgressResetResponse {
  /** The progress row that was reset, or null if the learner had none. */
  progressId: number | null;
  /** Preserved by the reset — a restart undoes completion, not enrollment. */
  startedAt: string | null;
}

// Response from POST /api/progress/learning-objects/{id}/checkpoint
export interface CheckpointResponse {
  step: string;
  progressId: number;
  timestamp: string;
}

// Per-course CTA status from GET /api/progress/learning-object-status
export interface CourseProgressStatus {
  hasCertificate: boolean;
  /** BPE-571: policy-backed — no longer "video finished is enough". */
  canClaimCertificate: boolean;
  isInProgress: boolean;
  /**
   * BPE-739: whether the user has finished the course. Distinct from the two
   * certificate fields, which only ever speak for credit-bearing content — a
   * Simulation earns no certificate, so this is the only thing that tells a
   * finished one from an untouched one.
   */
  isCompleted: boolean;
}

export type CourseStatusMap = Record<number, CourseProgressStatus>;

// Response item from GET /api/progress/learning-objects (list of in-progress)
export interface InProgressCourseItem {
  learningObjectId: number;
  title: string;
  durationMinutes: number;
  /** The kinds the course holds, as canonical codes, alphabetical (e.g. `["CLE", "CPE"]`). */
  variantKinds: string[];
  /** Accreditation types the course grants on-demand, alphabetical (e.g. `["CLE", "CPE"]`). */
  accreditationTypes?: string[];
  progressPercent: number;
  startedAt: string | null;
  lastAccessedAt: string | null;
  thumbnailUrl: string | null;
  deliveryMode: DeliveryMode;
  /** Original recording / live event date — drives the "Recorded …" line on dashboard cards. */
  originalLiveDate?: string | null;
}

export const progressApi = {
  /**
   * Get all in-progress courses (started but not completed).
   * Returns both delivery modes — two items for the same course when both tracks are active.
   */
  getInProgressCourses: async (): Promise<InProgressCourseItem[]> => {
    const response = await api.get<{ items: InProgressCourseItem[] }>('/progress/learning-objects');
    return response.data.items;
  },

  /**
   * Get user's progress on a specific course and delivery mode.
   */
  getCourseProgress: async (learningObjectId: number, deliveryMode?: DeliveryMode): Promise<CourseProgressResponse> => {
    const params = deliveryMode ? { deliveryMode } : {};
    const response = await api.get<CourseProgressResponse>(`/progress/learning-objects/${learningObjectId}`, { params });
    return response.data;
  },

  /**
   * Mark course as started. Stores the learner's credit type choice and delivery mode.
   */
  startCourse: async (
    learningObjectId: number,
    creditType: string | null,
    timezone?: string,
    deliveryMode?: DeliveryMode,
    variant?: string,
  ): Promise<CourseStartResponse> => {
    const response = await api.post<CourseStartResponse>(`/progress/learning-objects/${learningObjectId}/start`, {
      creditType,
      timezone,
      deliveryMode,
      variant,
    });
    return response.data;
  },

  /**
   * Update progress during video playback.
   *
   * Hardening Unit 4 (P0 #4): the legacy `completed` flag was removed from the
   * server contract. Stamping `VideoCompletedAt` happens exclusively via the
   * dedicated POST /complete endpoint. Client-set completion is no longer
   * accepted by the API.
   *
   * Hardening Unit 9 (P1 #17): callers can pass an AbortSignal so the UI can
   * cancel an in-flight stale save when a fresher one starts. Pre-Unit-9 a
   * slow save could land AFTER a newer save with a higher position, racing
   * against it server-side. Server-side the UPDATE is now atomic monotonic-
   * max so even if both lands the data is correct, but cancelling stale
   * requests still saves bandwidth and avoids spurious "save failed" toasts.
   */
  updateProgress: async (
    learningObjectId: number,
    data: { currentPosition: number; progressPercent: number; videoDurationSeconds?: number },
    deliveryMode?: DeliveryMode,
    signal?: AbortSignal,
  ): Promise<ProgressUpdateResponse> => {
    const params = deliveryMode ? { deliveryMode } : {};
    const response = await api.put<ProgressUpdateResponse>(
      `/progress/learning-objects/${learningObjectId}`,
      data,
      { params, signal },
    );
    return response.data;
  },

  /**
   * Persist delivery-specific resume/runtime state (e.g. SCORM session state) as an
   * opaque JSON string. Last-write-wins; returns 204. Designed for fire-and-forget
   * calls from the SCORM LMSCommit path, so callers can pass an AbortSignal to cancel
   * a stale in-flight save and may ignore the (empty) result.
   */
  updateProgressData: async (
    learningObjectId: number,
    progressData: string | null,
    deliveryMode?: DeliveryMode,
    signal?: AbortSignal,
  ): Promise<void> => {
    const params = deliveryMode ? { deliveryMode } : {};
    await api.put(
      `/progress/learning-objects/${learningObjectId}/progress-data`,
      { progressData },
      { params, signal },
    );
  },

  /**
   * Mark a checkpoint step as completed.
   */
  completeCheckpoint: async (learningObjectId: number, step: string, deliveryMode?: DeliveryMode): Promise<CheckpointResponse> => {
    const params = deliveryMode ? { deliveryMode } : {};
    const response = await api.post<CheckpointResponse>(
      `/progress/learning-objects/${learningObjectId}/checkpoint`,
      { step },
      { params },
    );
    return response.data;
  },

  /**
   * Mark video as completed.
   */
  completeVideo: async (learningObjectId: number, deliveryMode?: DeliveryMode): Promise<VideoCompleteResponse> => {
    const params = deliveryMode ? { deliveryMode } : {};
    const response = await api.post<VideoCompleteResponse>(
      `/progress/learning-objects/${learningObjectId}/complete`,
      null,
      { params },
    );
    return response.data;
  },

  /**
   * Undo completion after the learner started a new attempt inside Apex
   * (BPE-722, R6.1).
   *
   * Simulation deliveries only — the server refuses anything else, because the
   * same call against a CLE or CPE delivery would clear a learner's completion
   * and with it the basis of their certificate. Do not reach for this as a
   * general "reset progress"; there deliberately isn't one.
   *
   * Keeps the learner started: only the completion state is cleared.
   */
  resetSimulationProgress: async (
    learningObjectId: number,
    deliveryMode?: DeliveryMode,
  ): Promise<SimulationProgressResetResponse> => {
    const params = deliveryMode ? { deliveryMode } : {};
    const response = await api.post<SimulationProgressResetResponse>(
      `/progress/learning-objects/${learningObjectId}/reset`,
      null,
      { params },
    );
    return response.data;
  },

  /**
   * Get CTA status for multiple courses in a single request.
   */
  getCourseStatus: async (learningObjectIds: number[]): Promise<CourseStatusMap> => {
    if (learningObjectIds.length === 0) return {};
    const response = await api.get<CourseStatusMap>('/progress/learning-object-status', {
      params: { learningObjectIds: learningObjectIds.join(',') },
    });
    return response.data;
  },
};
